<template>
  <div class="asc-health-score-plot-wrapper">
    <template v-if="!isEmpty">
      <asc-stock-chart :options="getChartOptions"></asc-stock-chart>
    </template>
    <asc-no-data v-if="isEmpty"></asc-no-data>
    <asc-filter-legend
      class="asc-health-score-plot-wrapper--legend"
      :items="getLegendItems"
      :on-item-click="handleLegendItemClick"
      :items-selected="legendItemsSelected"
    ></asc-filter-legend>
  </div>
</template>

<script>
import AscStockChart from '@/components/molecules/AscStockChart'
import AscFilterLegend from '@/components/molecules/AscFilterLegend'
import AscNoData from '@/components/atoms/AscNoData'
import { getFormattedUTCDateTime } from '@/utilities/dates'

export default {
  name: 'AscHealthScorePlotWrapper',

  components: {
    AscStockChart,
    AscFilterLegend,
    AscNoData,
  },

  props: {
    plotData: {
      type: Object,
      default: () => {},
    },
    phmAlertData: {
      type: Array,
      default: () => [],
    },
    mxRecOverlaysData: {
      type: Array,
      default: () => [],
    },
    selectedItem: {
      type: String,
      default: '',
    },
    selectedFeature: {
      type: String,
      default: '',
    },
  },

  data() {
    const _that = this
    return {
      legendItemsSelected: [],
      defaultOptions: {
        chart: {
          type: 'scatter',
          marginRight: 30,
          animation: false,
          zoomType: 'xy',
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          series: {
            showInNavigator: false,
            gapSize: 1,
            states: {
              inactive: {
                opacity: 1,
              },
              hover: {
                enabled: true,
              },
            },
          },
        },
        rangeSelector: {
          enabled: true,
          inputEnabled: false,
        },
        series: [],
        time: {
          useUTC: true,
        },
        title: {
          text: '',
        },
        tooltip: {
          shared: true,
          useHTML: true,
          formatter: function () {
            if (this.series) {
              const formattedDate = getFormattedUTCDateTime(this.x)
              let tooltipString = ''
              if (this.series.name === 'Plotline') {
                tooltipString += 'Maintenance Rec Created: <br/>'
                tooltipString += formattedDate
              }

              if (this.series.name !== 'Plotline') {
                if (this.series.name === 'PHM Alert') {
                  tooltipString += 'PHM Alert: <br/>'
                }
                tooltipString +=
                  formattedDate +
                  '<br/>' +
                  _that.getYAxisLabel +
                  ': ' +
                  this.y.toFixed(2) +
                  _that.getPointAlert(this.series, this.x, this.y) +
                  _that.getPointFlightNumber(this.series, this.x, this.y) +
                  _that.getAnalyticVersionNumber(this.series, this.x, this.y)
              }
              return tooltipString
            }
          },
          positioner: function () {
            const pos = this.getPosition.apply(this, arguments)
            return pos
          },
        },
      },
    }
  },

  computed: {
    isEmpty() {
      if (this.plotData) {
        return (
          this.plotData.featureData?.length === 0 ||
          this.legendItemsSelected.length === 2
        )
      }
      return true
    },

    getYAxisLabel() {
      return `${this.selectedFeature} - ${this.selectedItem}`
    },

    getLegendItems() {
      return [
        {
          header: '',
          items: [
            {
              text: this.selectedFeature,
              styleColor: '#53B8B3',
              borderRadius: '50%',
            },
            {
              text: 'PHM Alert',
              styleColor: '#D20532',
              borderRadius: '50%',
              border: '4px solid #FFCCCB',
            },
          ],
        },
      ]
    },

    getChartOptions() {
      const xAxisOptions = this.buildXAxisOptions
      const yAxisOptions = this.buildYAxisOptions
      const navigator = this.buildNavigatorOptions
      const series = this.buildSeries

      const options = {
        ...this.defaultOptions,
        ...series,
        ...xAxisOptions,
        ...yAxisOptions,
        ...navigator,
      }

      return options
    },

    buildXAxisOptions() {
      return {
        xAxis: {
          type: 'datetime',
          crosshair: false,
          labels: {
            rotation: -90,
          },
        },
      }
    },

    buildYAxisOptions() {
      const _that = this

      return {
        yAxis: [
          {
            alignTicks: true,
            allowDecimals: true,
            gridLineWidth: 1,
            max: 2.2,
            min: -0.2,
            opposite: false,
            plotLines: [
              {
                color: '#d20532',
                dashStyle: 'shortdash',
                value: 1,
                width: 2,
              },
            ],
            tickInterval: 0.2,
            title: {
              text: _that.getYAxisLabel,
              margin: 30,
            },
            labels: {
              formatter: function () {
                if (this.value === -0.2 || this.value === 2.2) {
                  return ''
                }
                return this.value
              },
            },
          },
        ],
      }
    },

    buildNavigatorOptions() {
      return {
        navigator: {
          enabled: false,
        },
        scrollbar: {
          enabled: false,
        },
      }
    },

    buildSeries() {
      const series = []

      const featureData = this.plotData?.featureData || []
      const phmAlertData = this.phmAlertData || []

      series.push({
        name: this.selectedFeature,
        color: '#53B8B3',
        data: featureData.map(item => {
          return [new Date(item.timestamp).getTime(), item.point]
        }),
        marker: {
          radius: 2,
          symbol: 'circle',
        },
        type: 'scatter',
        visible: !this.legendItemsSelected.includes(this.selectedFeature),
        yAxis: 0,
        tooltip: {
          valueDecimals: 0,
        },
      })

      series.push({
        name: 'PHM Alert',
        data: phmAlertData.map(item => {
          return [new Date(item.timestamp).getTime(), item.value]
        }),
        color: '#D20532',
        marker: {
          radius: 4,
          symbol: 'circle',
          fillColor: '#D20532',
          lineColor: '#FFCCCB',
          lineWidth: 4,
        },
        type: 'scatter',
        visible: !this.legendItemsSelected.includes('PHM Alert'),
        yAxis: 0,
        tooltip: {
          valueDecimals: 0,
        },
      })

      this.mxRecOverlaysData.forEach((item, index) => {
        if (item.dateSubmitted) {
          const dateSubmitted = new Date(item.dateSubmitted).getTime()
          const overlays = []
          overlays.push([dateSubmitted, 2.2])
          overlays.push([dateSubmitted, -0.2])

          series.push({
            name: `Plotline${index}`,
            color: 'black',
            data: overlays,
            type: 'scatter',
            marker: {
              radius: 0,
              symbol: 'circle',
            },
            visible: true,
            yAxis: 0,
            tooltip: {
              valueDecimals: 0,
            },
          })
        }
      })

      return {
        series: series,
      }
    },
  },

  watch: {
    plotData: {
      immediate: true,
      handler(newData) {
        this.legendItemsSelected = []
        this.plotData = newData
      },
    },
  },

  methods: {
    getPointAlert(series, x, y) {
      if (series.name === 'PHM Alert') {
        const data = this.phmAlertData || []
        const [point] = data.filter(
          item => new Date(item.timestamp).getTime() === x && item.point === y,
        )
        return point && point.alert ? '<br/>' + `Alert: ${point.alert}` : ''
      } else {
        return ''
      }
    },

    getPointFlightNumber(series, x, y) {
      if (series.name === 'PHM Alert') {
        const data = this.phmAlertData || []
        const [point] = data.filter(
          item => new Date(item.timestamp).getTime() === x && item.point === y,
        )
        return point && point.flightNumber
          ? '<br/>' + `Flight: ${point.flightNumber}`
          : ''
      } else {
        const data = this.plotData?.featureData || []
        const [point] = data.filter(
          item => new Date(item.timestamp).getTime() === x && item.point === y,
        )
        return point && point.flightNumber
          ? '<br/>' + `Flight: ${point.flightNumber}`
          : ''
      }
    },

    getAnalyticVersionNumber(series, x, y) {
      const data =
        series.name === 'PHM Alert'
          ? this.phmAlertData || []
          : this.plotData?.featureData || []

      const [point] = data?.filter(
        item => new Date(item.timestamp).getTime() === x && item.point === y,
      )
      return point && point.version
        ? '<br/>' + `Analytic Version Number: ${point.version}`
        : ''
    },

    handleLegendItemClick(data) {
      const isFound = this.legendItemsSelected.includes(data)
      if (isFound) {
        this.legendItemsSelected = this.legendItemsSelected.filter(
          item => item !== data,
        )
      } else {
        this.legendItemsSelected.push(data)
      }
    },
  },
}
</script>

<style lang="scss">
.asc-health-score-plot-wrapper {
  &--legend {
    padding: 0;

    .asc-legend-list > ul {
      display: flex;

      .asc-legend-list-item {
        width: 30%;
      }
    }
  }

  .highcharts-reset-zoom {
    opacity: 0.4;
  }

  .highcharts-reset-zoom:hover {
    opacity: 1;
  }
}

.reset-zoom-btn {
  position: absolute;
  right: 50px;
  top: 5px;
}
</style>
